"use client";

import { useEffect, useRef, useState } from "react";
import type { Lookup } from "@/lib/tool-kit/poetry/pronounce";
import {
  loadPronouncingDictionary,
  type PronouncingDictionary,
} from "@/lib/tool-kit/vocabulary/pronouncing-dictionary";

export type DictionaryStatus = "idle" | "loading" | "ready" | "failed";

const NONE: Lookup = () => null;

/**
 * The CMU pronouncing dictionary as a `Lookup`, fetched once the first time `enabled`
 * turns true (the file is about 0.85 MB compressed, so a page that nobody types into
 * never pays for it). While it loads — or if it fails — the lookup finds nothing, and
 * every word is estimated from its spelling.
 */
export function usePoetryDictionary(enabled: boolean): {
  lookup: Lookup;
  status: DictionaryStatus;
} {
  const [dictionary, setDictionary] = useState<PronouncingDictionary | null>(
    null
  );
  const started = useRef(false);
  const [status, setStatus] = useState<DictionaryStatus>("idle");

  useEffect(() => {
    if (!enabled || started.current) {
      return;
    }
    started.current = true;
    setStatus("loading");
    loadPronouncingDictionary()
      .then((loaded) => {
        setDictionary(loaded);
        setStatus("ready");
      })
      .catch(() => setStatus("failed"));
  }, [enabled]);

  const lookup: Lookup = dictionary ? (key) => dictionary.lookup(key) : NONE;
  return { lookup, status };
}
